// Zustand store for the solo gameplay screen. Holds the live
// SoloSessionState + PlayerProfile, plus action helpers that POST
// to the server and merge the returned state.
//
// Store responsibilities (kept narrow):
//   - Cache the SoloSessionState as it changes round-to-round.
//   - Wrap each /api/solo/* fetch and update state.shop atomically.
//   - Surface the most recent BattleResult so SoloPlayScreen can
//     hand it to BattleScreen and clear it when the replay finishes.
//
// What it does NOT do:
//   - Mutate state locally before the server confirms (server is
//     authoritative; round-trip latency is fine for now).
//   - Manage routing — SoloPlayScreen owns the screen-state machine.

import { create } from 'zustand'
import type {
  BattleResult,
  PlayerProfile,
  SoloBattleResponse,
  SoloSessionState,
  UnitDef,
} from '@origami/shared'
import {
  TRANSIENT_SHOP_ERROR_CODES,
  friendlyShopErrorMessage,
} from '@origami/shared'
import { playSfx } from '../sound'
import {
  buyFromShop,
  combineUnits,
  readyForBattle,
  reorderUnits,
  rerollShop,
  sellUnit,
  startSolo,
  toggleShopLock,
} from './soloApi'
import { logoutPlayer } from './playerApi'
import { type BuffFloater, diffBoardForFloaters } from './buffFloaters'

export type { BuffFloater }

type Phase = 'idle' | 'loading' | 'shop' | 'battle' | 'finished' | 'error'

interface SoloState {
  player: PlayerProfile | null
  session: SoloSessionState | null
  phase: Phase
  /** Last successful battle replay — set by `ready()` so
   *  SoloPlayScreen can transition to BattleScreen. Cleared by
   *  `dismissBattle()` when the replay finishes. */
  pendingBattle: { replay: BattleResult; meta: SoloBattleResponse['meta'] } | null
  /** Hard / session-fatal error code surfaced by an action. Null when
   *  the previous action succeeded. Only session-level codes (e.g.
   *  no_session) land here — recoverable action failures go to the
   *  transient `notice` channel below so the player stays in the
   *  shop instead of flipping to an error phase. */
  error: string | null
  /** Transient action-failure notice (e.g. "Not enough gold for this
   *  card."). Set by `applyError` for codes in TRANSIENT_SHOP_ERROR_CODES;
   *  rendered as a brief toast by SoloPlayScreen. Auto-cleared by the
   *  toast component on dismiss / timeout, or by the next successful
   *  action via `applyState`. */
  notice: { code: string; message: string; at: number } | null

  /** Unit catalog cache keyed by unit_id. Fetched once on screen
   *  mount; used to map unit_id → glyph for the replay screen
   *  (BattleEvent payloads carry unit_ids only). */
  unitsById: Record<string, UnitDef>

  /** Floating HP/ATK deltas queued by the most recent `applyState`
   *  diff. Shop.tsx renders these as transient floaters; consumers
   *  dismiss each via `clearBuffFloater(id)` once its animation
   *  finishes. */
  buffFloaters: BuffFloater[]

  setPlayer: (p: PlayerProfile | null) => void
  loadCatalog: () => Promise<void>
  start: () => Promise<void>
  buy: (slot_index: number) => Promise<void>
  /** Drag-from-shop landing on a specific board slot. If the target
   *  is occupied by a same-unit-same-star copy, sequences
   *  /buy → /combine on the server so the dropped slot becomes the
   *  combine source. Otherwise falls through to a plain buy. */
  buyToSlot: (
    slot_index: number,
    target: { where: 'active' | 'bench'; position: number },
    opts?: { floaterDelayMs?: number },
  ) => Promise<void>
  sell: (where: 'active' | 'bench', position: number) => Promise<void>
  reroll: () => Promise<void>
  toggleLock: (slot_index: number) => Promise<void>
  reorder: (
    from: { where: 'active' | 'bench'; position: number },
    to: { where: 'active' | 'bench'; position: number },
  ) => Promise<void>
  combine: (
    from: { where: 'active' | 'bench'; position: number },
    to: { where: 'active' | 'bench'; position: number },
    opts?: { floaterDelayMs?: number },
  ) => Promise<void>
  ready: () => Promise<void>
  dismissBattle: () => void
  /** Dismiss the current transient notice (toast click / timeout). */
  clearNotice: () => void
  /** Remove a queued floater after its animation finishes. */
  clearBuffFloater: (id: string) => void
  /** Clear the player cookie server-side AND reset all local store
   *  state so the next mount routes through the player-creation
   *  flow. Called from RunComplete on "New Run" / "Title Screen". */
  logout: () => Promise<void>
}

export const useSoloStore = create<SoloState>((set, get) => {
  /** Apply a SoloSessionState from a successful state-only response.
   *  Centralized so every action handler updates the phase enum +
   *  clears any prior error consistently. Also diffs the previous
   *  session's board against the new one and queues a BuffFloater
   *  for each unit whose HP/ATK climbed while keeping its identity
   *  (same unit_id + same star + same slot). This catches
   *  on_purchase / on_combine_witnessed / on_shop_round_pass buffs
   *  without coupling the shop UI to specific trigger code. */
  function applyState(
    state: SoloSessionState,
    opts?: { floaterDelayMs?: number },
  ) {
    const phase: Phase = state.phase === 'done' ? 'finished' : 'shop'
    const prev = get().session
    // Round income — a new shop round opened with more gold than the
    // last (passive income + win bonus). Coin-shuffle into the pouch.
    if (prev && state.shop.round > prev.shop.round && state.shop.gold > prev.shop.gold) {
      playSfx('gold-earn')
    }
    const newFloaters = prev
      ? diffBoardForFloaters(prev.shop, state.shop, { delayMs: opts?.floaterDelayMs })
      : []
    const existing = get().buffFloaters
    set({
      session: state,
      phase,
      error: null,
      notice: null,
      buffFloaters: newFloaters.length > 0 ? [...existing, ...newFloaters] : existing,
    })
  }

  /** Map an ApiResult failure into store state. Recoverable shop-action
   *  failures (insufficient_gold, cannot_combine, etc.) become a
   *  transient toast notice so the player stays in the shop; only
   *  session-fatal codes flip the phase to 'error'. */
  function applyError(code: string) {
    if (TRANSIENT_SHOP_ERROR_CODES.has(code)) {
      set({
        notice: { code, message: friendlyShopErrorMessage(code), at: Date.now() },
      })
      return
    }
    set({ error: code, phase: 'error' })
  }

  return {
    player: null,
    session: null,
    phase: 'idle',
    pendingBattle: null,
    error: null,
    notice: null,
    unitsById: {},
    buffFloaters: [],

    setPlayer: (p) => set({ player: p }),

    clearBuffFloater: (id) => {
      set({ buffFloaters: get().buffFloaters.filter((f) => f.id !== id) })
    },

    /** Fetch /api/units once and cache by id. Idempotent — early-
     *  returns when the cache is already populated. Failures are
     *  silent because catalog absence only degrades visuals
     *  (glyph fallback to 'crane') rather than blocking gameplay. */
    async loadCatalog() {
      if (Object.keys(get().unitsById).length > 0) return
      try {
        const res = await fetch('/api/units', { credentials: 'include' })
        if (!res.ok) return
        const units = (await res.json()) as UnitDef[]
        const byId: Record<string, UnitDef> = {}
        for (const u of units) byId[u.id] = u
        set({ unitsById: byId })
      } catch {
        // Network blip on a cosmetic resource — let the next mount retry.
      }
    },

    async start() {
      set({ phase: 'loading', error: null })
      const res = await startSolo()
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state)
    },

    async buy(slot_index) {
      const res = await buyFromShop({ slot_index })
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state)
    },

    async buyToSlot(slot_index, target, opts) {
      // Single atomic call: the server folds the fresh ★1 into the
      // target if it's a same-unit-same-star copy, or places it at the
      // (empty) target slot, or rejects with cannot_combine. No empty
      // slot anywhere on the team is required when the target is a
      // valid combine partner, which is the whole point — the player
      // can fill an 8-unit team and still drag a shop card onto an
      // existing copy to upgrade it.
      const res = await buyFromShop({ slot_index, target })
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state, opts)
    },

    async sell(where, position) {
      const res = await sellUnit({ where, position })
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state)
    },

    async reroll() {
      const res = await rerollShop()
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state)
    },

    async toggleLock(slot_index) {
      const res = await toggleShopLock({ slot_index })
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state)
    },

    async reorder(from, to) {
      const res = await reorderUnits({ from, to })
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state)
    },

    async combine(from, to, opts) {
      const res = await combineUnits({ from, to })
      if (!res.ok) return applyError(res.code)
      applyState(res.value.state, opts)
    },

    async ready() {
      const cur = get().session
      if (!cur) return
      set({ phase: 'loading', error: null })
      const res = await readyForBattle()
      if (!res.ok) return applyError(res.code)
      // The /ready response carries the replay AND the next-round
      // shop state. Stash the replay; transition phase to 'battle'.
      // BattleScreen renders it and calls dismissBattle() on
      // completion to swing back to 'shop' (or 'finished').
      // ALWAYS transition to the battle phase so the player sees the
      // replay of the round they just queued — including the final
      // 10th-win or 0-HP-loss battle. dismissBattle reads
      // session.phase to decide whether to swing back to 'shop' or
      // jump to 'finished' once the replay completes.
      set({
        pendingBattle: { replay: res.value.replay, meta: res.value.meta },
        session: res.value.state,
        phase: 'battle',
        error: null,
      })
    },

    dismissBattle() {
      // Drop the replay and return to the shop view. If the run ended
      // (Match.status=finished server-side), phase stays 'finished'.
      const s = get()
      const next: Phase = s.session?.phase === 'done' ? 'finished' : 'shop'
      set({ pendingBattle: null, phase: next })
    },

    clearNotice() {
      set({ notice: null })
    },

    async logout() {
      // Clear the cookie server-side. The /me probe will then return
      // 401 and the SoloRoute fork routes the user to
      // PlayerCreationScreen for a fresh identity. We also wipe local
      // store state so no stale player/session/catalog survives the
      // transition.
      await logoutPlayer()
      set({
        player: null,
        session: null,
        phase: 'idle',
        pendingBattle: null,
        error: null,
        buffFloaters: [],
      })
    },
  }
})
